import { Capacitor } from '@capacitor/core';
import { disableBiometric as disableNativeBiometric } from 'services/nativeBiometric';

/**
 * Clear the web biometric cookies/localStorage entries for this device.
 * Cookie-clearing logic lives here (not inline per-caller) so logout,
 * password/security reset, and the Settings toggle-off all wipe the exact
 * same set of keys — a caller that misses one leaves a dead credential a
 * player can't see or clear themselves.
 */
function clearWebBiometricState(playerEmail?: string): void {
  const parts = window.location.hostname.split('.');
  const domain = parts.length >= 2 ? parts.slice(-2).join('.') : '';
  const domainAttr = domain ? `domain=.${domain}; ` : '';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; ';
  document.cookie = `hj_passkey=; path=/; ${domainAttr}${expired}SameSite=Lax${secure}`;
  document.cookie = `hj_passkey_email=; path=/; ${domainAttr}${expired}SameSite=Lax${secure}`;
  document.cookie = `hj_biometric_rt=; path=/; ${domainAttr}${expired}SameSite=Lax${secure}`;
  document.cookie = `hj_biometric_cred=; path=/; ${domainAttr}${expired}SameSite=Lax${secure}`;
  if (playerEmail) {
    try {
      localStorage.removeItem(`hj_biometric_rt_${playerEmail}`);
    } catch {
      /* ignore */
    }
    try {
      localStorage.removeItem(`hj_biometric_cred_${playerEmail}`);
    } catch {
      /* ignore */
    }
  }
}

/**
 * Clear whatever biometric credential this device holds — native keystore
 * entry or web cookies/localStorage, whichever applies. Call on logout,
 * password/security reset, remote device revocation, or an explicit
 * Settings disable (EN-5415): a stale credential left behind after any of
 * these stays usable to sign back in until the player happens to notice and
 * turn biometrics off themselves.
 */
export async function clearBiometricCredentials(playerEmail?: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    await disableNativeBiometric();
  } else {
    clearWebBiometricState(playerEmail);
  }
}
